import { useEPGService } from '@/app/context/DIContext/DIServices'
import Channel from '@/shared/classes/channels/channel'
import { EpgObject, ExtendedEpgElement, RawEpgObject } from '@/types/epg/Epg'
import { RawProgramObject } from '@/types/epg/Program'
import { epgChannelRequest } from '@/types/epg/epgChannelRequest'
import Program from '@/shared/classes/epg/program/program'
import { checkIfProgramCurrentlyRuns } from '@/shared/classes/epg/helpers/isProgramRunning'
import {
    getFormattedProgramDuration,
    getProgramDurationProgress,
} from '@/shared/classes/epg/helpers/programDurationHelpers'
import { filterProgramsByTime } from '@/shared/hooks/EPG/lib'
import {
    CACHE_TRIM_AMOUNT,
    MILIS_24h,
    PROGRAMS_LENGTH_LIMIT,
} from '@/shared/constants'
import { IEPGServiceToya } from '@/shared/services/types/epgService/epgServiceInterfaces'
import { getPrograms } from '@/shared/classes/epg/helpers/getPrograms'
import { getErrorMessage } from '@/shared/lib'

const useEPGRepository = () => {
    const {
        createExtendedEpg,
        filterWidevineOttEpg,
        sortEpgElements,
        rawProgramsToFullPrograms,
    } = useEPGService()

    const createEpg = (channels: Channel[], apiEpg: RawEpgObject[]) =>
        createExtendedEpg(channels, apiEpg)

    const filterEpgList = (epg: ExtendedEpgElement[], channels: Channel[]) =>
        filterWidevineOttEpg(epg, channels)

    const sortEpgList = (array: ExtendedEpgElement[]) => sortEpgElements(array)
    const createFullProgramObject = (array: RawProgramObject[]) =>
        rawProgramsToFullPrograms(array)

    const fetchEpgData = async (
        fetchProgramsFunc: (
            channelsParams?: epgChannelRequest[],
            isPresentFollowing?: boolean
        ) => Promise<RawEpgObject[]>,
        channelsParams?: epgChannelRequest[],
        isPresentFollowing?: boolean
    ): Promise<EpgObject[]> => {
        if (!channelsParams && !isPresentFollowing) return []
        const response = await fetchProgramsFunc(
            channelsParams,
            isPresentFollowing
        )
        if (Array.isArray(response) && response.length) {
            return response.map(({ channelId, programs }) => ({
                channelId,
                programs: createFullProgramObject(programs),
            }))
        }
        return []
    }

    const getUpdatedPrograms = (
        programs: Program[],
        newPrograms: Program[],
        direction: string | null,
        getCurrentProgramFunc: (programs: Program[]) => {
            currentProgram: Program | undefined
            programDurationProgress: number
            programFormattedDuration: string
        },
        milis24h: number,
        filterProgramsByTime: (
            programs: Program[],
            startTimeBoundary: number
        ) => Program[]
    ) => {
        switch (direction) {
            case 'up':
                return [...newPrograms, ...programs]
            case 'down':
                return [...programs, ...newPrograms]
            case 'firstRender': {
                const { currentProgram } = getCurrentProgramFunc([
                    ...programs,
                    ...newPrograms,
                ])
                return currentProgram
                    ? filterProgramsByTime(
                          newPrograms,
                          currentProgram.startMilis - milis24h
                      )
                    : newPrograms
            }
            default:
                return programs
        }
    }

    const trimPrograms = (
        programs: Program[],
        direction: string | null,
        trimAmount: number
    ) => {
        if (direction === 'up') {
            const endTrimPoint = trimAmount
            return programs.slice(0, programs.length - endTrimPoint)
        } else {
            const startTrimPoint = trimAmount
            return programs.slice(startTrimPoint)
        }
    }

    const getCurrentProgram = (fullyNamesPrograms: Program[]) => {
        let currentProgram: Program | undefined
        let programDurationProgress = 0
        let programFormattedDuration = ''

        if (fullyNamesPrograms.length !== 0) {
            fullyNamesPrograms.forEach((fullyNamedProgram: Program) => {
                const { startMilis, endMilis, durationMilis } =
                    fullyNamedProgram
                if (checkIfProgramCurrentlyRuns(startMilis, endMilis) === 0) {
                    currentProgram = fullyNamedProgram
                    programDurationProgress = Math.floor(
                        getProgramDurationProgress(startMilis, durationMilis)
                    )
                    programFormattedDuration = getFormattedProgramDuration(
                        startMilis,
                        endMilis
                    )
                }
            })
        }
        return {
            currentProgram,
            programDurationProgress,
            programFormattedDuration,
        }
    }

    const updateEpgDataWithNewPrograms = (
        epgData: ExtendedEpgElement[],
        convertedResponse: {
            channelId: string
            programs: Program[]
        }[],
        direction: string | null
    ): ExtendedEpgElement[] =>
        epgData.map((epgChannel) => {
            const update = convertedResponse.find(
                (item) => item.channelId === epgChannel.channelId
            )

            if (!update) return epgChannel

            let updatedPrograms = getUpdatedPrograms(
                epgChannel.programs,
                update.programs,
                direction,
                getCurrentProgram,
                MILIS_24h,
                filterProgramsByTime
            )

            if (updatedPrograms.length > PROGRAMS_LENGTH_LIMIT) {
                updatedPrograms = trimPrograms(
                    updatedPrograms,
                    direction,
                    CACHE_TRIM_AMOUNT
                ).sort(
                    (programA, programB) =>
                        programA.startMilis - programB.startMilis
                )
            }

            return {
                ...epgChannel,
                programs: updatedPrograms,
            }
        })

    const mergeEpgArrays = (
        arr1: ExtendedEpgElement[],
        arr2: ExtendedEpgElement[]
    ): ExtendedEpgElement[] => {
        const mergedMap = new Map<string, ExtendedEpgElement>()

        const addOrMerge = (element: ExtendedEpgElement) => {
            const existingElement = mergedMap.get(element.channelId)

            if (existingElement) {
                const uniquePrograms = new Map<string, Program>()

                existingElement.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )

                element.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )

                existingElement.programs = Array.from(uniquePrograms.values())
            } else {
                const uniquePrograms = new Map<string, Program>()
                element.programs.forEach((program) =>
                    uniquePrograms.set(program.id, program)
                )
                element.programs = Array.from(uniquePrograms.values())

                mergedMap.set(element.channelId, element)
            }
        }

        arr1.forEach(addOrMerge)
        arr2.forEach(addOrMerge)

        return Array.from(mergedMap.values())
    }

    const prepareEPGforSetToState = async (
        channels: Channel[],
        createExtendedEpg: IEPGServiceToya['createExtendedEpg'],
        filterWidevineOttEpg: IEPGServiceToya['filterWidevineOttEpg'],
        sortEpgElements: IEPGServiceToya['sortEpgElements']
    ) => {
        try {
            const channelsId = channels.map((channel) => channel.id)
            const apiEpg = await getPrograms(channelsId)
            if (!apiEpg) throw new TypeError('Parameter is not a RawEpgObject!')
            const rawRemoteEpg = createExtendedEpg(channels, apiEpg)
            if (rawRemoteEpg) {
                const widevineOttEpg = filterWidevineOttEpg(
                    rawRemoteEpg,
                    channels
                )
                const sortedEpg = sortEpgElements(widevineOttEpg)
                return sortedEpg
            }
        } catch (error) {
            getErrorMessage('setEpg', error)
            throw error
        }
    }

    return {
        mergeEpgArrays,
        createEpg,
        filterEpgList,
        sortEpgList,
        createFullProgramObject,
        fetchEpgData,
        getUpdatedPrograms,
        trimPrograms,
        getCurrentProgram,
        updateEpgDataWithNewPrograms,
        prepareEPGforSetToState,
    }
}

export { useEPGRepository }
